// PublicDownload.js - Öffentliche Download-Seite für Share Links
import BodyScrollLock from './components/BodyScrollLock';
import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';

function PublicDownload() {
  const { shareId: token } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const adminBypass = searchParams.get('admin_bypass') === 'true';
  const adminJwt = searchParams.get('token') || '';

  const [fileInfo, setFileInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordVerified, setPasswordVerified] = useState(false);

  // Abuse Report State
  const [showAbuseModal, setShowAbuseModal] = useState(false);
  const [abuseType, setAbuseType] = useState('');
  const [abuseDescription, setAbuseDescription] = useState('');
  const [abuseEmail, setAbuseEmail] = useState('');
  const [abuseSubmitting, setAbuseSubmitting] = useState(false);
  const [abuseSuccess, setAbuseSuccess] = useState(false);

  useEffect(() => {
    loadFileInfo();
  }, [token]);

  const loadFileInfo = async () => {
    try {
      const bypassSuffix = adminBypass ? `?admin_bypass=true&token=${encodeURIComponent(adminJwt)}` : '';
      const response = await fetch(`/api/share/info/${token}${bypassSuffix}`);
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Link ungültig');
      }
      
      const data = await response.json();
      setFileInfo(data);
      setRequiresPassword(data.requiresPassword || false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    if (password.length < 4) {
      setPasswordError('Passwort muss mindestens 4 Zeichen haben');
      return;
    }
    
    try {
      const response = await fetch(`/api/share/verify-password/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (data.code === 'INVALID_PASSWORD') {
          setPasswordError('❌ Falsches Passwort');
        } else {
          setPasswordError(data.error || 'Fehler bei der Überprüfung');
        }
        return;
      }
      
      setPasswordVerified(true);
      setPasswordError('');
    } catch (err) {
      setPasswordError('Verbindungsfehler');
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    
    try {
      let downloadUrl;
      if (adminBypass) {
        downloadUrl = `/api/share/download/${token}?admin_bypass=true&token=${encodeURIComponent(adminJwt)}`;
      } else if (requiresPassword && password) {
        downloadUrl = `/api/share/download/${token}?password=${encodeURIComponent(password)}`;
      } else {
        downloadUrl = `/api/share/download/${token}`;
      }
      
      const response = await fetch(downloadUrl);
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Download fehlgeschlagen');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileInfo.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      await loadFileInfo();
    } catch (err) {
      alert(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleAbuseSubmit = async () => {
    if (!abuseType) return;
    setAbuseSubmitting(true);
    try {
      const res = await fetch('/api/abuse/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          share_token: token,
          report_type: abuseType,
          description: abuseDescription.trim() || undefined,
          reporter_email: abuseEmail.trim() || undefined,
        }),
      });
      if (res.ok) setAbuseSuccess(true);
    } catch (err) {
      console.error('[ABUSE] Submit error:', err);
    } finally {
      setAbuseSubmitting(false);
    }
  };

  const closeAbuseModal = () => {
    setShowAbuseModal(false);
    setAbuseType('');
    setAbuseDescription('');
    setAbuseEmail('');
    setAbuseSuccess(false);
    setAbuseSubmitting(false);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Lade Datei-Informationen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-orange-600/20 rounded-2xl blur-xl"></div>
            <div className="relative bg-slate-900 border border-white/10 rounded-2xl p-8 text-center">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Link ungültig</h2>
              <p className="text-slate-400 mb-6">{error}</p>
              <a
                href="/"
                className="inline-block px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all"
              >
                Zur Startseite
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-600/20 rounded-2xl blur-xl"></div>
            
            <div className="relative bg-slate-900 border border-white/10 rounded-2xl p-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl mb-4">
                  <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                </div>
                <h1 className="text-3xl font-bold text-white mb-2">Datei zum Download bereit</h1>
                <p className="text-slate-400">Geteilt über AVS Cloud</p>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
                <div className="flex items-start gap-4">
                  <div className="text-5xl">📄</div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-semibold text-white mb-2 truncate">
                      {fileInfo.filename}
                    </h2>
                    <div className="space-y-1 text-sm text-slate-400">
                      <p>Größe: <span className="text-white">{formatBytes(fileInfo.file_size)}</span></p>
                      {fileInfo.uploader && (
                        <p>Hochgeladen von: <span className="text-white">{fileInfo.uploader}</span></p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-br from-cyan-500/10 to-transparent border border-cyan-400/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs text-slate-400">Gültig bis</span>
                  </div>
                  <p className="text-sm font-semibold text-white">
                    {formatDate(fileInfo.expires_at)}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-400/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span className="text-xs text-slate-400">Downloads</span>
                  </div>
                  <p className="text-sm font-semibold text-white">
                    {fileInfo.downloads_remaining !== null 
                      ? `${fileInfo.downloads_remaining} verbleibend`
                      : 'Unbegrenzt'
                    }
                  </p>
                </div>
              </div>

              {/* Password Prompt */}
              {requiresPassword && !passwordVerified ? (
                <form onSubmit={handlePasswordSubmit} className="mb-6">
                  <div className="bg-gradient-to-br from-purple-500/10 to-pink-600/10 border border-purple-400/30 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Passwort erforderlich</h3>
                        <p className="text-sm text-slate-400">Diese Datei ist passwortgeschützt</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setPasswordError('');
                        }}
                        placeholder="Passwort eingeben"
                        className="w-full px-4 py-3 bg-white/5 border border-purple-400/30 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/10 transition-all"
                        autoFocus
                      />
                      
                      {passwordError && (
                        <p className="text-sm text-red-400">{passwordError}</p>
                      )}

                      <button
                        type="submit"
                        className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all active:scale-95"
                      >
                        Passwort überprüfen
                      </button>
                    </div>
                  </div>
                </form>
              ) : null}

              <button
                onClick={handleDownload}
                disabled={downloading || (requiresPassword && !passwordVerified)}
                className="w-full px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Download läuft...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Datei herunterladen
                  </span>
                )}
              </button>

              <div className="mt-6 text-center">
                <p className="text-xs text-slate-500 mb-2">
                  Sicherer Download über AVS Cloud
                </p>
                <a
                  href="/"
                  className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  Erstelle deinen eigenen Cloud-Speicher →
                </a>
              </div>

              {/* ── Abuse-Melde-Button (§18 DDG / NetzDG Pflicht) ── */}
              <div className="mt-6 pt-5 border-t border-white/5 text-center">
                <button
                  onClick={() => setShowAbuseModal(true)}
                  className="text-xs text-slate-500 hover:text-red-400 transition-colors inline-flex items-center gap-1.5"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  Inhalt melden
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Abuse Report Modal */}
      {showAbuseModal && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem'}}>
          <BodyScrollLock />
          <div style={{maxWidth: '440px', width: '100%', background: '#0d1117', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '2rem'}}>
            {abuseSuccess ? (
              <div style={{textAlign: 'center', padding: '1rem 0'}}>
                <div style={{fontSize: '2.5rem', marginBottom: '0.75rem'}}>✅</div>
                <h3 style={{fontWeight: 800, fontSize: '1.1rem', color: '#fff', marginBottom: '0.5rem', fontFamily: 'sans-serif'}}>Meldung erhalten</h3>
                <p style={{color: '#8b9cbf', fontSize: '0.875rem', lineHeight: 1.7, marginBottom: '1.5rem'}}>
                  Danke für deine Meldung. Sie wird geprüft (klare Fälle: sofort; komplexe: max. 7 Tage).
                </p>
                <button onClick={closeAbuseModal} style={{padding: '0.75rem 2rem', background: 'rgba(26,180,240,0.1)', border: '1px solid rgba(26,180,240,0.3)', borderRadius: '10px', color: '#1ab4f0', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600}}>Schließen</button>
              </div>
            ) : (
              <>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                    <div style={{width: '38px', height: '38px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem'}}>🚨</div>
                    <div>
                      <h3 style={{fontWeight: 700, fontSize: '1rem', color: '#fff', margin: 0}}>Inhalt melden</h3>
                      <p style={{fontSize: '0.7rem', color: '#8b9cbf', margin: 0, textTransform: 'uppercase', letterSpacing: '0.06em'}}>Missbrauchsmeldung</p>
                    </div>
                  </div>
                  <button onClick={closeAbuseModal} style={{background: 'none', border: 'none', color: '#8b9cbf', cursor: 'pointer', fontSize: '1.1rem'}}>✕</button>
                </div>

                <div style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#8b9cbf', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.5rem'}}>Art der Meldung *</label>
                  <select
                    value={abuseType}
                    onChange={e => setAbuseType(e.target.value)}
                    style={{width: '100%', padding: '0.7rem 1rem', background: 'rgba(10,31,61,0.6)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: abuseType ? '#fff' : '#8b9cbf', fontSize: '0.875rem', outline: 'none', cursor: 'pointer'}}
                  >
                    <option value="" disabled>Bitte wählen...</option>
                    <option value="illegal_content">Illegaler Inhalt</option>
                    <option value="copyright">Urheberrechtsverletzung</option>
                    <option value="privacy">Datenschutzverletzung</option>
                    <option value="spam">Spam</option>
                    <option value="other">Sonstiges</option>
                  </select>
                </div>

                <div style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#8b9cbf', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.5rem'}}>Beschreibung <span style={{fontWeight: 400, textTransform: 'none', letterSpacing: 0}}>(optional, max. 500 Zeichen)</span></label>
                  <textarea
                    value={abuseDescription}
                    onChange={e => { if (e.target.value.length <= 500) setAbuseDescription(e.target.value); }}
                    placeholder="Kurze Beschreibung des Problems..."
                    rows={3}
                    style={{width: '100%', padding: '0.7rem 1rem', background: 'rgba(10,31,61,0.6)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: '#fff', fontSize: '0.875rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box'}}
                  />
                  <p style={{fontSize: '0.72rem', color: '#8b9cbf', textAlign: 'right', marginTop: '0.25rem'}}>{abuseDescription.length}/500</p>
                </div>

                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#8b9cbf', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.5rem'}}>E-Mail für Rückmeldung <span style={{fontWeight: 400, textTransform: 'none', letterSpacing: 0}}>(optional)</span></label>
                  <input
                    type="email"
                    value={abuseEmail}
                    onChange={e => setAbuseEmail(e.target.value)}
                    placeholder="deine@email.de"
                    style={{width: '100%', padding: '0.7rem 1rem', background: 'rgba(10,31,61,0.6)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: '#fff', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box'}}
                  />
                </div>

                <div style={{display: 'flex', gap: '0.75rem'}}>
                  <button
                    onClick={handleAbuseSubmit}
                    disabled={!abuseType || abuseSubmitting}
                    style={{flex: 1, padding: '0.75rem', background: abuseType ? 'rgba(239,68,68,0.15)' : 'rgba(100,100,100,0.1)', border: `1px solid ${abuseType ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '10px', color: abuseType ? '#f87171' : '#8b9cbf', cursor: abuseType ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: '0.875rem', transition: 'all 0.2s'}}
                  >
                    {abuseSubmitting ? 'Sende...' : 'Meldung absenden'}
                  </button>
                  <button onClick={closeAbuseModal} style={{padding: '0.75rem 1.25rem', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', color: '#8b9cbf', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600}}>Abbrechen</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default PublicDownload;
